import { useCallback, useEffect, useState } from 'react';
import {
  enableNotifications,
  pushEnvironment,
  refreshEnvironment,
  type PushEnvironment,
} from './push.ts';

const DISMISS_KEY = 'notifications-banner-dismissed';

interface NotificationsState {
  /** The CTA is worth showing: push can work here and has not been decided yet. */
  canPrompt: boolean;
  /** iOS in a browser tab — offer the add-to-home-screen guidance instead. */
  needsHomeScreen: boolean;
  environment: PushEnvironment;
  /** The last outcome, in Russian, for the banner to show. */
  message: string | null;
  busy: boolean;
  enable: () => Promise<void>;
  dismiss: () => void;
}

/**
 * Mirrors usePwaInstall: the banner is hidden for the session once dismissed,
 * so it can return on the next launch rather than being gone for good.
 */
export function useNotifications(): NotificationsState {
  const [environment, setEnvironment] = useState<PushEnvironment>(pushEnvironment);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Adding to the home screen relaunches the app, but a desktop install can
  // flip display-mode under a live page — at which point iOS guidance would be
  // wrong and the real CTA should appear.
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const refresh = () => setEnvironment(pushEnvironment());
    media.addEventListener('change', refresh);
    return () => media.removeEventListener('change', refresh);
  }, []);

  // The one thing the synchronous read cannot know: iOS answers about its own
  // permission asynchronously, so on the app this is what decides whether the
  // banner has anything to offer. A no-op on the web.
  useEffect(() => {
    void refreshEnvironment().then(setEnvironment);
  }, []);

  const enable = useCallback(async () => {
    setBusy(true);
    const result = await enableNotifications();
    setMessage(result.message);
    setEnvironment(await refreshEnvironment());
    setBusy(false);
    // A refusal leaves the banner up with the reason; success has nothing left
    // to say, so the banner goes away on its own.
    if (result.ok) {
      sessionStorage.setItem(DISMISS_KEY, '1');
      setDismissed(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  const undecided = environment.permission === 'default';
  const relevant = environment.needsHomeScreen || (environment.supported && undecided);

  return {
    canPrompt: relevant && !dismissed,
    needsHomeScreen: environment.needsHomeScreen,
    environment,
    message,
    busy,
    enable,
    dismiss,
  };
}
